/**
 * Voice Helper — ElevenLabs-backed TTS via /api/voice/speak.
 * NO browser SpeechSynthesis fallback (was causing overlapping/repetition).
 * Used across activities so Pre-K children can hear content without reading.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _currentAudio = null;
let _currentKey = null;
const _cache = new Map();
const _MAX_CACHE = 100;

const _getToken = () =>
  localStorage.getItem('littlelearners-token') ||
  localStorage.getItem('token') ||
  '';

const _stop = () => {
  if (_currentAudio) {
    try {
      _currentAudio.pause();
      _currentAudio.currentTime = 0;
    } catch {}
    _currentAudio = null;
    _currentKey = null;
  }
  // Belt-and-suspenders: kill any residual browser TTS if anything ever calls it
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
};

export const speak = async (text, rate = 0.95, onEnd = null) => {
  if (!text || !text.trim() || !BACKEND_URL) {
    if (onEnd) onEnd();
    return;
  }
  const cleanText = text.substring(0, 4000);
  const voice = 'nova';
  const speed = Math.max(0.7, Math.min(1.2, rate));
  const key = `${cleanText}::${voice}::${speed}`;

  // Same audio already playing → skip (prevents repetition on quick re-renders)
  if (_currentKey === key && _currentAudio && !_currentAudio.paused) return;

  _stop();

  try {
    let audioUrl = _cache.get(key);
    if (!audioUrl) {
      const token = _getToken();
      const resp = await fetch(
        `${BACKEND_URL}/api/voice/speak?text=${encodeURIComponent(cleanText)}&voice=${voice}&speed=${speed}`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!resp.ok) throw new Error(`TTS ${resp.status}`);
      const blob = await resp.blob();
      audioUrl = URL.createObjectURL(blob);

      if (_cache.size >= _MAX_CACHE) {
        const oldestKey = _cache.keys().next().value;
        const oldUrl = _cache.get(oldestKey);
        try { URL.revokeObjectURL(oldUrl); } catch {}
        _cache.delete(oldestKey);
      }
      _cache.set(key, audioUrl);
    }

    const audio = new Audio(audioUrl);
    _currentAudio = audio;
    _currentKey = key;

    audio.onended = () => {
      if (_currentAudio === audio) {
        _currentAudio = null;
        _currentKey = null;
      }
      if (onEnd) onEnd();
    };
    audio.onerror = () => {
      if (_currentAudio === audio) {
        _currentAudio = null;
        _currentKey = null;
      }
    };
    await audio.play().catch(() => {});
  } catch (err) {
    console.warn('ElevenLabs TTS failed silently:', err?.message || err);
  }
};

export const speakSlow = (text, onEnd = null) => speak(text, 0.8, onEnd);
export const speakInstruction = (text, onEnd = null) => speak(text, 0.9, onEnd);
export const stopSpeaking = () => _stop();
export const isTTSSpeaking = () => Boolean(_currentAudio && !_currentAudio.paused);
